import React from 'react';
import { TableCell } from '@mui/material';
import type { AuditIntegrationCase, AuditItem } from '../../../types/features/storeDetails/legacyAudits';
import { formatCurrency } from '../../../utils/formatUtils';
import { prepareScratcherCountsDetails, prepareScratcherSalesDetails } from '../utils/legacyAuditDiscrepancyDetails';
import { extractLegacyChildRowValues } from '../utils/legacyAuditChildRowValues';
import CalculatedValueTableCell from './CalculatedValueTableCell';

type ColumnType = 'scratcherCounts' | 'lottoSales' | 'scratcherSales' | 'payout' | 'total';

type ColumnConfig = {
  type: ColumnType;
  columnIndex: number;
};

const COLUMN_CONFIGS: Record<AuditIntegrationCase, ColumnConfig[]> = {
  bothConnected: [
    { type: 'scratcherCounts', columnIndex: 7 },
    { type: 'scratcherSales', columnIndex: 8 },
    { type: 'lottoSales', columnIndex: 9 },
    { type: 'payout', columnIndex: 10 },
    { type: 'total', columnIndex: 11 },
  ],
  bothNotConnected: [
    { type: 'scratcherCounts', columnIndex: 7 },
    { type: 'scratcherSales', columnIndex: 8 },
  ],
  lottoConnectedOnly: [
    { type: 'scratcherCounts', columnIndex: 7 },
    { type: 'scratcherSales', columnIndex: 8 },
    { type: 'lottoSales', columnIndex: 9 },
    { type: 'payout', columnIndex: 10 },
  ],
  posConnectedOnly: [
    { type: 'scratcherCounts', columnIndex: 7 },
    { type: 'scratcherSales', columnIndex: 8 },
    { type: 'lottoSales', columnIndex: 9 },
    { type: 'payout', columnIndex: 10 },
  ],
};

type LegacyAuditChildRowConditionalColumnsProps = {
  item: AuditItem;
  integrationCase: AuditIntegrationCase;
  hasPosType?: boolean;
  groupBorderColor: string;
  innerBorderColor: string;
};

const LegacyAuditChildRowConditionalColumns: React.FC<LegacyAuditChildRowConditionalColumnsProps> = ({
  item,
  integrationCase,
  hasPosType = true,
  groupBorderColor,
  innerBorderColor,
}) => {
  const showDiscrepancy = integrationCase === 'bothConnected';
  const values = extractLegacyChildRowValues(item);

  const scratcherCountsDetails = prepareScratcherCountsDetails(
    values.posScratcherQuantity,
    values.totalSold,
    item?.reconciliation_variance?.scratcher?.quantity_variance,
    values.isPosZero,
    values.isLottoZero,
    hasPosType,
    showDiscrepancy,
  );

  const scratcherSalesDetails = prepareScratcherSalesDetails(
    values.posScratcherSales,
    values.revenue,
    item?.reconciliation_variance?.scratcher?.revenue_variance,
    values.isPosZero,
    values.isLottoZero,
    hasPosType,
    showDiscrepancy,
  );

  const config = COLUMN_CONFIGS[integrationCase];
  const innerBorderStyle = { borderRight: `1px solid ${innerBorderColor}` };
  const groupBorderStyle = { borderRight: `1px solid ${groupBorderColor}` };

  return (
    <>
      {config.map((col, index) => {
        const isGroupEnd = index === config.length - 1;
        const borderStyle = isGroupEnd ? groupBorderStyle : innerBorderStyle;
        const childSx = { opacity: 0.6, ...borderStyle, textAlign: 'right' as const };

        if (col.type === 'scratcherCounts') {
          return (
            <CalculatedValueTableCell
              key={col.columnIndex}
              details={scratcherCountsDetails}
              isChildRow
              borderRightColor={isGroupEnd ? groupBorderColor : innerBorderColor}
              formatFunction={(value) => value.toString()}
            />
          );
        }

        if (col.type === 'scratcherSales') {
          return (
            <CalculatedValueTableCell
              key={col.columnIndex}
              details={scratcherSalesDetails}
              isChildRow
              borderRightColor={isGroupEnd ? groupBorderColor : innerBorderColor}
            />
          );
        }

        if (col.type === 'lottoSales') {
          return (
            <TableCell key={col.columnIndex} sx={childSx}>
              {formatCurrency(values.posLottoSales)}
            </TableCell>
          );
        }

        if (col.type === 'payout') {
          return (
            <TableCell key={col.columnIndex} sx={childSx}>
              {formatCurrency(values.posPayout)}
            </TableCell>
          );
        }

        return (
          <TableCell key={col.columnIndex} sx={childSx}>
            <span style={{ color: '#666' }}>-</span>
          </TableCell>
        );
      })}
    </>
  );
};

export default LegacyAuditChildRowConditionalColumns;
